import { useEffect, useState } from "react";
import User from "@/Layouts/User";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import Container from "@/Components/User/Common/Container";
import { useRouter } from "next/router";
import { encryptInteger } from "@/Utils/encryptInteger";
import { useBuy } from "@/context/buy";
import BuyTicketModal from "@/Components/User/Modal/BuyTicketModal";
import { loadGoogleMapsScript } from "@/Utils/loadScript";
import Head from "next/head";
import Cookies from "js-cookie";
import { AnimatePresence, motion } from "framer-motion";
import { formatMonthDayYear } from "@/Utils/dateFormat";
import MetaTags from "@/Components/SEO/MetaTags";
import { Schema } from "@/Components/SEO/Schema";
import withBaseUrl from "@/Utils/const";
import AnimatedReveal from "@/Components/HOC/AnimatedReveal";
import { useLoading } from "@/context/loading";

const L = typeof window !== "undefined" ? require("leaflet") : null;
const MarkerClusterGroup =
  typeof window !== "undefined" ? require("leaflet.markercluster") : null;

export default function Index({ shows, API_KEY, tsc }) {
  const router = useRouter();
  const [leaflet, setLeaflet] = useState(null);
  const [map, setMap] = useState(null);
  const {
    openSingleModal,
    openModal,
    setLoggedUserDetails,
    selectedData,
    setSelectedData,
    selectedUser,
    setSelectedUser,
    setCustomModalData,
  } = useBuy();
  const { isInitialLoad } = useLoading();

  useEffect(() => {
    if (typeof window !== "undefined") {
      Promise.all([import("leaflet"), import("leaflet.markercluster")]).then(
        ([L]) => {
          setLeaflet(L);
        }
      );
    }
  }, []);

  useEffect(() => {
    if (tsc && tsc == 400) {
      localStorage.clear();
      sessionStorage.clear();
      Cookies.remove("user_data");
      setLoggedUserDetails({
        data: "",
        status: false,
        remove: true,
      });
    }
  }, [tsc]);

  useEffect(() => {
    if (API_KEY) {
      loadGoogleMapsScript(API_KEY);
    }
  }, [API_KEY]);

  useEffect(() => {
    if (leaflet) {
      const usBounds = leaflet.latLngBounds([
        [49.3457868, -125.0],
        [24.396308, -66.93457],
      ]);

      const position = [39.0902, -100.7129];
      const zoomLevel = 4.5;

      const newMap = leaflet.map("map", {
        center: position,
        zoom: zoomLevel,
        maxBounds: usBounds,
        maxBoundsViscosity: 1.0,
        layers: [
          leaflet.tileLayer(
            "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
            {
              attribution:
                '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
              maxZoom: 18,
            }
          ),
        ],
      });

      setMap(newMap);
      return () => {
        newMap.remove();
      };
    }
  }, [leaflet]);

  useEffect(() => {
    if (map && shows?.length > 0 && leaflet) {
      const markerClusterGroup = L.markerClusterGroup();

      shows.forEach((show) => {
        if (show.show.venue?.latitude && show.show.venue?.longitude) {
          const marker = leaflet.marker(
            [show.show.venue.latitude, show.show.venue.longitude],
            {
              icon: leaflet.icon({
                iconUrl: show.profile,
                iconSize: [30, 30],
                iconAnchor: [12.5, 25],
              }),
            }
          );
          markerClusterGroup.addLayer(marker);
        }
      });

      map.addLayer(markerClusterGroup);

      return () => {
        if (map.hasLayer(markerClusterGroup)) {
          markerClusterGroup.clearLayers();
          map.removeLayer(markerClusterGroup);
        }
      };
    }
  }, [map, shows, leaflet]);

  useEffect(() => {
    if (map) {
      const handleZoomEnd = () => {
        if (map.getZoom() < 3.5) {
          map.setZoom(3.5);
        }
      };
      map.on("zoomend", handleZoomEnd);

      return () => {
        map.off("zoomend", handleZoomEnd);
      };
    }
  }, [map]);

  const slugify = (str) =>
    str
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  const getBreadcrumbs = () => {
    return [
      {
        name: "Home",
        url: withBaseUrl("/"),
      },
      {
        name: "Tours",
        url: withBaseUrl("/tours"),
      },
    ];
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const mapVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, delay: 0.2 },
    },
  };

  const rowVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: isInitialLoad ? 1.2 + 0.1 * i : 0.1 * i,
      },
    }),
  };

  return (
    <div>
      <User>
        <MetaTags
          title="Tours | Qoncert"
          description="Explore music tours and concert series. Find and book tickets for upcoming tour dates."
          ogImage="/assets/logo.webp"
          canonicalUrl="/tours"
        />
        <Head>
          <title>Tours | Qoncert</title>
          <Schema breadcrumbs={getBreadcrumbs()} />
        </Head>
        <Container
          id="tours"
          className="relative px-6 py-4 lg:pl-[75px] lg:pr-[48px] lg:py-[32px]"
        >
          {openModal.buy && (
            <AnimatePresence>
              <div className="absolute">
                <BuyTicketModal
                  showData={selectedData}
                  currentArtist={selectedUser.id}
                />
              </div>
            </AnimatePresence>
          )}

          <motion.div
            variants={titleVariants}
            initial="hidden"
            animate="visible"
            className="py-6 heading flex items-center justify-center"
          >
            Touring Now
          </motion.div>

          <motion.div
            variants={mapVariants}
            initial="hidden"
            animate="visible"
            className="h-[240px] lg:h-[620px] relative"
          >
            <section className="map-component h-full">
              <div
                id="map"
                className="toursMap aspect-[2/1]"
                style={{ height: "100%" }}
              ></div>
            </section>
          </motion.div>

          <div className="my-10">
            {shows && shows.length == 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: isInitialLoad ? 1.2 : 0.4 }}
              >
                <div>
                  <p className="text-[#F9F9F9] text-xl font-[700] text-center mb-0 lg:text-2xl leading-[48px]">
                    No Tours Scheduled
                  </p>
                  <p className="text-white text-base text-center lg:text-lg font-[600] mt-4 lg:mt-11">
                    Check back again
                  </p>
                </div>
              </motion.div>
            ) : (
              <>
                <div className="pt-10 hidden lg:block">
                  <table
                    id="tours-table"
                    className="w-full rounded-lg overflow-hidden shadow-sm"
                  >
                    <thead className="thead-bg font-urbanist">
                      <tr>
                        <td className="text-center"></td>
                        <td className="text-center">Artist Name</td>
                        <td className="text-center">Hometown</td>
                        <td className="text-center">Current Stop</td>
                        <td className="text-center">Date</td>
                        <td className="text-center">Tickets</td>
                      </tr>
                    </thead>
                    <tbody className="tbody-bg">
                      {shows &&
                        shows.map((val, idx) => {
                          const formattedDate = formatMonthDayYear(
                            val.show.show_date
                          );
                          const city = slugify(val?.show?.venue?.city);
                          const state = slugify(val?.show?.venue?.state);
                          const showName = slugify(val?.show?.name);
                          const venueName = slugify(val?.show?.venue?.name);
                          const showDate = val?.show?.show_date;

                          console.log('val', val)

                          const fullPath = `/shows/${city}-${state}/${val.show.id}-for-${showName}-at-${venueName}-on-${showDate}`;
                          return (
                            <motion.tr
                              key={idx}
                              custom={idx}
                              variants={rowVariants}
                              initial="hidden"
                              animate="visible"
                              onClick={() => router.push(fullPath)}
                              className="t-5 cursor-pointer font-urbanist"
                            >
                              <td
                                onClick={(e) => {
                                  e.stopPropagation();
                                  router.push(`/artist/${val.username}`);
                                }}
                                className="pt-6 px-3 tours-td image-td"
                              >
                                <img
                                  className="w-[67px] h-[60px] rounded-xl object-cover "
                                  src={val.profile}
                                  alt=""
                                />
                              </td>
                              <td className="artist tours-td pt-6">
                                <p>{val.name}</p>
                              </td>
                              <td className="hometown tours-td pt-6">
                                <p>
                                  {val?.city}, {val?.state}{" "}
                                </p>
                              </td>
                              <td className="stop tours-td pt-6">
                                <p>
                                  {val?.show?.venue?.city},{" "}
                                  {val?.show?.venue?.state}
                                </p>
                              </td>
                              <td className="date tours-td pt-6">
                                {formattedDate}
                              </td>
                              <td className="tickets">
                                <div className="flex flex-col lg:flex-row gap-3 w-full">
                                  {val?.show?.is_ticket_booked && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        router.push(`/tickets/${val.show.booking_id}`);
                                      }}
                                      className="view_ticket_btn w-full lg:w-2/4 rounded-[10px]"
                                    >
                                      <p className="px-3 py-3 font-bold">View Ticket</p>
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();

                                      const show = val?.show;
                                      if (!show) return;

                                      if (show.venue_ticket_web_link) {
                                        openSingleModal("custom");
                                        setCustomModalData({
                                          showId: show.id,
                                          link: show.venue_ticket_web_link,
                                        });
                                        return;
                                      }

                                      if (show?.venue?.can_buy_tickets) {
                                        const key = `qoncert_cart_${show.id}`;
                                        const obj = {
                                          [val.is_national ? "national_id" : "artist_id"]: val.id,
                                          ticket_count: 1,
                                          user_id: val.id,
                                          is_national: !!val.is_national,
                                        };

                                        let cart = JSON.parse(localStorage.getItem(key)) || [];
                                        if (!Array.isArray(cart)) cart = [];
                                        cart.push(obj);
                                        localStorage.setItem(key, JSON.stringify(cart));

                                        openSingleModal("buy");
                                        setSelectedData(show);
                                        setSelectedUser(val);
                                      }
                                    }}
                                    className={`
                                      ${val?.show?.is_ticket_booked ? "lg:min-w-2/4" : "w-full"} 
                                      ${val?.show?.venue?.can_buy_tickets ? "main_btn_bg" : "disableBuyTicket"} 
                                      rounded-[10px] min-h-11 max-h-11 min-w-max font-bold
                                    `}
                                  >
                                    <p className="flex justify-center pl-3 pr-3 min-w-max">
                                      Get Tickets
                                    </p>
                                  </button>
                                </div>
                              </td>
                            </motion.tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
                <div className="pt-5 lg:hidden">
                  <table
                    id="tours-table"
                    className="w-full rounded-lg overflow-hidden shadow-sm"
                  >
                    <tbody className="tbody-bg">
                      {shows &&
                        shows.map((val, idx) => {
                          const city = slugify(val?.show?.venue?.city);
                          const state = slugify(val?.show?.venue?.state);
                          const showName = slugify(val?.show?.name);
                          const venueName = slugify(val?.show?.venue?.name);
                          const showDate = val?.show?.show_date;
                          const fullPath = `/shows/${city}-${state}/${val.show.id}-for-${showName}-at-${venueName}-on-${showDate}`;
                          return (
                            <motion.tr
                              key={idx}
                              custom={idx}
                              variants={rowVariants}
                              initial="hidden"
                              animate="visible"
                              onClick={() => router.push(fullPath)}
                              className="t-5 cursor-pointer font-urbanist"
                            >
                              <td
                                onClick={(e) => {
                                  e.stopPropagation();
                                  router.push(`/artist/${val.username}`);
                                }}
                                className="pt-6 px-3 tours-td image-td"
                              >
                                <img
                                  className="w-[67px] h-[60px] rounded-xl object-cover "
                                  src={val.profile}
                                  alt=""
                                />
                              </td>
                              <td className="artist text-start tours-td pt-6">
                                <p className="text-start">{val.name}</p>
                                <p className="text-start text-[#9E9E9E]">
                                  {formatMonthDayYear(val.show.show_date)}
                                </p>
                                <p className="text-start text-[#9E9E9E] ">
                                  {val.show?.venue?.city},{" "}
                                  {val.show?.venue?.state}
                                </p>
                              </td>
                              <td className="tickets">
                                <div className="flex flex-col lg:flex-row gap-2 w-full">
                                  {val?.show?.is_ticket_booked && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        router.push(`/tickets/${val.show.booking_id}`);
                                      }}
                                      className="view_ticket_btn w-full lg:w-3/4 rounded-[10px]"
                                    >
                                      <p className="px-3 text-[10px] min-w-max py-2 font-semibold">View Ticket</p>
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const show = val?.show;
                                      if (!show) return
                                      if (show.venue_ticket_web_link) {
                                        openSingleModal("custom");
                                        setCustomModalData({
                                          showId: show.id,
                                          link: show.venue_ticket_web_link,
                                        });
                                        return;
                                      }
                                      if (show?.venue?.can_buy_tickets) {
                                        const key = `qoncert_cart_${show.id}`;
                                        const obj = {
                                          [val.is_national ? "national_id" : "artist_id"]: val.id,
                                          ticket_count: 1,
                                          user_id: val.id,
                                          is_national: !!val.is_national,
                                        };
                                        let cart = JSON.parse(localStorage.getItem(key)) || [];
                                        if (!Array.isArray(cart)) cart = [];
                                        cart.push(obj);
                                        localStorage.setItem(key, JSON.stringify(cart));
                                        openSingleModal("buy");
                                        setSelectedData(show);
                                        setSelectedUser(val);
                                      }
                                    }}
                                    className={`
                                      ${val?.show?.is_ticket_booked ? "lg:w-1/4" : "w-full"} 
                                      ${val?.show?.venue?.can_buy_tickets ? "main_btn_bg" : "disableBuyTicket"} 
                                      rounded-[10px] font-semibold
                                    `}
                                  >
                                    <p className="px-3 text-[10px] min-w-max py-2">
                                      Get Tickets
                                    </p>
                                  </button>
                                </div>
                              </td>
                            </motion.tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </Container>
      </User>
    </div>
  );
}

export async function getServerSideProps(context) {
  const rawCookies = context.req.headers.cookie || "";
  console.log({ rawCookies });
  let headers = {};

  const parsedCookies = Object.fromEntries(
    rawCookies.split("; ").map((cookie) => cookie.split("="))
  );
  console.log({ parsedCookies });

  let userData = {};
  if (parsedCookies.user_data) {
    try {
      userData = JSON.parse(decodeURIComponent(parsedCookies.user_data));
      console.log({ userData });
      headers = { Authorization: `Bearer ${userData?.web_token}` };
    } catch (error) {
      console.error("Error parsing user_data:", error);
    }
  }

  const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL;
  const API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY;

  if (!BACKEND_URL) {
    console.error("Missing environment variables: BACKEND_URL or API_TOKEN");
    return { props: { shows: [], API_KEY: null } };
  }

  const formData = new FormData();
  formData.append("action", "getArtistsByTour");

  try {
    const [response] = await Promise.all([
      fetch(`${BACKEND_URL}/webapi/manageTour`, {
        method: "POST",
        body: formData,
        headers,
      }),
    ]);
    console.log({ response });

    if (!response.ok) {
      console.error("Error fetching data from the backend");
      return { props: { shows: [], API_KEY: null } };
    }

    const [data] = await Promise.all([response.json()]);
    console.log("tours data", { data });

    return {
      props: {
        shows: data?.data || [],
        API_KEY: API_KEY,
        tsc: data?.tsc || null,
      },
    };
  } catch (error) {
    console.error("Error in getServerSideProps:", error);
    return {
      props: { shows: [], API_KEY: null },
    };
  }
}
